import React from "react";
import { FieldError } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import clsx from "clsx";
import { CountryData } from "react-phone-input-2";

import { getClassByVariant } from "helpers/getClassByVariant";
import styles from "./style.module.scss";

interface IProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: string | React.ReactNode;
  className?: string;
  inputClass?: string;
  labelClass?: string;
  wrapperClass?: string;
  error?: FieldError;
  isHideError?: boolean;
  disableCountryCode?: boolean;
  placeholder?: string;
  color?: "primary" | "secondary";
  disabled?: boolean;
}

export default function PhoneNumber({
  value,
  onChange,
  label,
  className,
  inputClass,
  labelClass,
  wrapperClass,
  error,
  placeholder = "",
  disableCountryCode = true,
  isHideError = false,
  color = "primary",
  disabled,
  ...props
}: Readonly<IProps>) {
  const usDialCode = "1";
  const displayValue = disableCountryCode ? value ?? "" : `${usDialCode}${value ?? ""}`;

  return (
    <div className={clsx(styles.smartPhoneInputContainer, className)}>
      {label && (
        <label
          className={clsx(
            styles.smartPhoneInputLabel,
            labelClass,
            getClassByVariant(color)?.label
          )}>
          {label}
        </label>
      )}
      <div className={styles.smartPhoneInputWrapper}>
        <PhoneInput
          {...props}
          value={displayValue}
          onChange={(val, data: CountryData) => {
            // US-specific normalization: strips leading country code "1" when disableCountryCode is true
            // This assumes US phone numbers and removes the leading "1" from the stored value
            // Conditions for stripping "1": disableCountryCode=true AND digits starts with "1"
            // Note: This will also strip any number starting with "1", not just valid US country codes
            const digits = val?.replace(/\D/g, "") ?? "";
            const dialCode = data?.dialCode ?? usDialCode;
            let normalized = digits;

            if (disableCountryCode && digits.startsWith(usDialCode)) {
              // Remove leading US "1" when country code is disabled
              normalized = digits.slice(usDialCode.length);
            } else if (!disableCountryCode && dialCode && digits.startsWith(dialCode)) {
              // Remove explicit dial code when country code is enabled
              normalized = digits.slice(dialCode.length);
            }
            onChange?.(normalized);
          }}
          disabled={disabled}
          disableDropdown
          disableSearchIcon
          disableInitialCountryGuess
          country={"us"}
          onlyCountries={["us"]}
          placeholder={placeholder || "(555) 555-5555"}
          specialLabel={""}
          disableCountryCode={disableCountryCode}
          inputClass={clsx(
            styles.smartPhoneInput,
            inputClass,
            getClassByVariant(color)?.input,
            !!error && "!border-b-error",
            "disabled:bg-inputDark",
            "disabled:cursor-not-allowed"
          )}
          containerClass={wrapperClass}
        />
      </div>
      {error && !isHideError && (
        <span className={styles.smartPhoneInputError}>{error.message}</span>
      )}
    </div>
  );
}
